// src/Pages/Disclaimer.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ShieldAlert, TrendingDown, Cpu, Briefcase, Calendar } from 'lucide-react';

const Disclaimer: React.FC = () => {
  return (
    <section className="relative min-h-screen bg-white py-20 px-4 sm:px-6 lg:px-8 font-[Urbanist] overflow-hidden">
      
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Top Hero Section: Image Left, Content Right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          
          {/* Left Side: Image */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-1 flex justify-start"
          >
            <img 
              src="https://nationalinsurance.nic.co.in/sites/default/files/styles/webp_converter/public/2024-06/Group%2045843%201.png.webp?itok=d3G-nQ_Z" 
              alt="Legal Disclaimer" 
              className="object-contain w-full max-w-lg lg:max-w-full h-[350px] md:h-[450px] drop-shadow-2xl"
            />
          </motion.div>

          {/* Right Side: Content */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-2 lg:order-2"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-200 text-red-600 text-sm font-bold tracking-wide mb-6">
              <Calendar size={16} /> Last Updated: April 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-6 leading-tight">
              Legal <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-500">Disclaimer</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Trading involves substantial risk and is not suitable for every investor. Please review our disclaimers carefully to understand the risks associated with algorithmic trading and financial markets.
            </p>
          </motion.div>
          
        </div>

        {/* Important Warning Banner */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-red-50 border border-red-200 p-6 sm:p-8 rounded-3xl mb-12 flex flex-col sm:flex-row gap-6 items-start sm:items-center shadow-lg shadow-red-100/50"
        >
          <div className="bg-red-100 p-4 rounded-2xl text-red-600 shrink-0">
            <ShieldAlert size={40} className="animate-pulse" />
          </div>
          <div>
            <h3 className="font-extrabold text-red-700 text-xl mb-2 flex items-center gap-2">
              Important Market Warning
            </h3>
            <p className="text-red-900/80 text-base leading-relaxed font-medium">
              Trading in the stock market, particularly in Futures & Options (F&O), involves a high degree of risk. You should fully understand these risks before investing your hard-earned capital. We provide technological tools and platforms; however, we do not guarantee any profits or returns.
            </p>
          </div>
        </motion.div>

        {/* Bottom Content Sections */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="space-y-6"
        >
          {/* Section 1 */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-md shadow-slate-100/50 hover:shadow-lg transition-shadow">
            <h2 className="text-2xl font-bold text-slate-900 mb-3 flex items-center gap-3">
              <div className="p-3 rounded-xl bg-slate-100 text-blue-600"><Briefcase size={24} /></div>
              1. No Financial Advice
            </h2>
            <p className="pl-16 text-slate-600 text-lg leading-relaxed">
              Motilal Algo operates strictly as a technology provider. We are <strong>not</strong> SEBI registered investment advisors. Any strategies, insights, or data presented on this website are exclusively for educational purposes. While we may provide trading signals based on algorithms, the ultimate decision to execute any trade rests entirely with you.
            </p>
          </div>

          {/* Section 2 */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-md shadow-slate-100/50 hover:shadow-lg transition-shadow">
            <h2 className="text-2xl font-bold text-slate-900 mb-3 flex items-center gap-3">
              <div className="p-3 rounded-xl bg-slate-100 text-emerald-600"><Cpu size={24} /></div>
              2. Execution Risk
            </h2>
            <p className="pl-16 text-slate-600 text-lg leading-relaxed">
              Our platform relies on automated algorithmic technology. Please be aware that occasionally, order execution may be delayed or fail entirely due to unforeseen technical factors such as internet outages, server glitches, or broker API downtime. Motilal Algo cannot be held liable for any financial losses resulting from such technical anomalies.
            </p>
          </div>

          {/* Section 3 */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-md shadow-slate-100/50 hover:shadow-lg transition-shadow">
            <h2 className="text-2xl font-bold text-slate-900 mb-3 flex items-center gap-3">
              <div className="p-3 rounded-xl bg-slate-100 text-orange-600"><TrendingDown size={24} /></div>
              3. Past Performance
            </h2>
            <p className="pl-16 text-slate-600 text-lg leading-relaxed">
              Any historical profits or backtested results displayed on our platform are not indicative of future returns. The financial markets are inherently volatile, and past market trends do not guarantee or define future performance.
            </p>
          </div>
        </motion.div>

        {/* Footer Text */}
        <div className="text-center mt-12 pt-8 border-t border-slate-200">
          <p className="text-slate-500 font-semibold tracking-wide">© {new Date().getFullYear()} Motilal Algo. Trade Responsibly & Stay Safe.</p>
        </div>
        
      </div>
    </section>
  );
};

export default Disclaimer;